import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import { Twitter, CheckCircle, XCircle, Clock, Send, Mail, Webhook, Edit3, Trash2, RefreshCw, Plus, Settings } from 'lucide-react';
import apiClient from '../services/api';
import { authService } from '../services/auth';
import { toast } from 'sonner';

export default function TwitterAgentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [connection, setConnection] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [posted, setPosted] = useState([]);
  const [intakeConfig, setIntakeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('drafts');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteSubject, setPasteSubject] = useState('');
  const [editingDraft, setEditingDraft] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchParams.get('connected') === '1') {
      toast.success('Twitter/X account connected');
    }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [profile, conn, draftsRes, postedRes, cfg] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/twitter/connection'),
        apiClient.get('/api/twitter/drafts'),
        apiClient.get('/api/twitter/posted'),
        apiClient.get('/api/twitter/intake/config'),
      ]);
      setUser({ ...profile.member, is_superadmin: profile.is_superadmin });
      setConnection(conn.data);
      setDrafts(draftsRes.data.drafts || []);
      setPosted(postedRes.data.posted || []);
      setIntakeConfig(cfg.data);
    } catch (err) {
      console.error('[Twitter] load failed', err);
      toast.error('Failed to load Twitter agent');
    }
    setLoading(false);
  };

  const handleConnect = async () => {
    try {
      const res = await apiClient.get('/api/twitter/oauth/start');
      window.location.href = res.data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Twitter OAuth not configured');
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect Twitter/X account?')) return;
    await apiClient.delete('/api/twitter/connection');
    toast.success('Disconnected');
    loadAll();
  };

  const handlePaste = async () => {
    if (!pasteText.trim()) return;
    try {
      await apiClient.post('/api/twitter/intake/paste', {
        text: pasteText,
        subject: pasteSubject || null,
        auto_post: false,
      });
      toast.success('Content ingested — draft created');
      setPasteText('');
      setPasteSubject('');
      setShowPasteModal(false);
      loadAll();
    } catch (err) {
      toast.error('Failed to ingest content');
    }
  };

  const handlePostNow = async (draftId) => {
    if (!confirm('Post this thread now?')) return;
    try {
      const res = await apiClient.post(`/api/twitter/drafts/${draftId}/post`);
      if (res.data.status === 'posted') {
        toast.success(`Posted ${res.data.tweet_ids.length} tweets`);
      } else {
        toast.error(`Post ${res.data.status}: ${res.data.error || ''}`);
      }
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Post failed');
    }
  };

  const handleSchedule = async (draftId, isoDateTime) => {
    try {
      await apiClient.put(`/api/twitter/drafts/${draftId}`, { scheduled_for: isoDateTime });
      toast.success('Scheduled');
      loadAll();
    } catch (err) {
      toast.error('Schedule failed');
    }
  };

  const handleCancelDraft = async (draftId) => {
    if (!confirm('Cancel this draft?')) return;
    await apiClient.delete(`/api/twitter/drafts/${draftId}`);
    loadAll();
  };

  const handlePollNow = async () => {
    try {
      const res = await apiClient.post('/api/twitter/intake/poll');
      toast.success(`Polled IMAP: ${res.data.ingested || 0} new emails`);
      loadAll();
    } catch (err) {
      toast.error('IMAP poll failed');
    }
  };

  const handleSaveSettings = async (newConfig) => {
    try {
      await apiClient.put('/api/twitter/intake/config', newConfig);
      toast.success('Settings saved');
      setShowSettings(false);
      loadAll();
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const handleSaveDraft = async (draftId, tweets) => {
    try {
      await apiClient.put(`/api/twitter/drafts/${draftId}`, { tweets });
      toast.success('Draft saved');
      setEditingDraft(null);
      loadAll();
    } catch (err) {
      toast.error('Save failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#3EF1C7]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      <MerchantHeader user={user} onLogout={async () => { await authService.logout(); navigate('/login'); }} />

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1DA1F2] to-[#7A5CFF] flex items-center justify-center">
              <Twitter className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Twitter/X Agent</h1>
              <p className="text-sm text-[#9CB0C7]">Auto-post Grok threads · Manage communities</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {connection?.connected ? (
              <>
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300">@{connection.username}</span>
                </div>
                <button onClick={handleDisconnect} className="px-3 py-2 rounded-md text-sm text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors">
                  Disconnect
                </button>
              </>
            ) : (
              <button onClick={handleConnect} className="px-4 py-2 rounded-md bg-[#1DA1F2] text-white text-sm font-semibold hover:bg-[#1DA1F2]/90 transition-colors flex items-center gap-2">
                <Twitter className="w-4 h-4" />
                Connect Twitter/X
              </button>
            )}
            <button onClick={() => setShowSettings(true)} className="p-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <button onClick={() => setShowPasteModal(true)} className="p-4 rounded-lg bg-[#0E1426] border border-[#1E3A5F] hover:border-[#3EF1C7] transition-colors text-left">
            <div className="flex items-center gap-2 mb-2">
              <Edit3 className="w-5 h-5 text-[#3EF1C7]" />
              <span className="font-semibold">Paste Content</span>
            </div>
            <p className="text-xs text-[#9CB0C7]">Drop in Grok email content — auto-formatted as a thread</p>
          </button>
          <button onClick={handlePollNow} disabled={!intakeConfig?.imap_enabled} className="p-4 rounded-lg bg-[#0E1426] border border-[#1E3A5F] hover:border-[#3EF1C7] transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-[#3EF1C7]" />
              <span className="font-semibold">Poll IMAP Now</span>
            </div>
            <p className="text-xs text-[#9CB0C7]">{intakeConfig?.imap_enabled ? 'Fetch new Grok emails' : 'Configure IMAP in Settings to enable'}</p>
          </button>
          <div className="p-4 rounded-lg bg-[#0E1426] border border-[#1E3A5F]">
            <div className="flex items-center gap-2 mb-2">
              <Webhook className="w-5 h-5 text-[#3EF1C7]" />
              <span className="font-semibold">Webhook URL</span>
            </div>
            <code className="text-[10px] text-[#9CB0C7] truncate block break-all">
              {intakeConfig?.webhook_url || 'Configure in Settings'}
            </code>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#1E3A5F] mb-4">
          <div className="flex gap-1">
            {['drafts', 'posted'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-[#3EF1C7] text-white'
                    : 'border-transparent text-[#9CB0C7] hover:text-white'
                }`}
              >
                {tab === 'drafts' ? `Drafts (${drafts.filter((d) => d.status === 'draft' || d.status === 'scheduled').length})` : `Posted (${posted.length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Drafts list */}
        {activeTab === 'drafts' && (
          <div className="space-y-3">
            {drafts.length === 0 ? (
              <div className="p-12 text-center text-[#9CB0C7]">
                No drafts yet. Paste content or configure Grok email intake to get started.
              </div>
            ) : (
              drafts.map((draft) => (
                <DraftCard
                  key={draft.draft_id}
                  draft={draft}
                  onPostNow={handlePostNow}
                  onSchedule={handleSchedule}
                  onCancel={handleCancelDraft}
                  onEdit={() => setEditingDraft(draft)}
                />
              ))
            )}
          </div>
        )}

        {/* Posted list */}
        {activeTab === 'posted' && (
          <div className="space-y-3">
            {posted.length === 0 ? (
              <div className="p-12 text-center text-[#9CB0C7]">No tweets posted yet.</div>
            ) : (
              posted.map((p) => (
                <div key={p.tweet_id} className="p-4 rounded-lg bg-[#0E1426] border border-[#1E3A5F]">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <p className="text-sm flex-1">{p.text}</p>
                    <a
                      href={`https://x.com/${connection?.username}/status/${p.tweet_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#3EF1C7] hover:underline flex-shrink-0"
                    >
                      View →
                    </a>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#9CB0C7]">
                    <span>{new Date(p.posted_at).toLocaleString()}</span>
                    <span>♥ {p.likes || 0}</span>
                    <span>↺ {p.retweets || 0}</span>
                    <span>💬 {p.replies || 0}</span>
                    <span>👁 {p.impressions || 0}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Paste modal */}
      {showPasteModal && (
        <PasteModal
          subject={pasteSubject}
          setSubject={setPasteSubject}
          text={pasteText}
          setText={setPasteText}
          onCancel={() => setShowPasteModal(false)}
          onSubmit={handlePaste}
        />
      )}

      {/* Edit draft modal */}
      {editingDraft && (
        <EditDraftModal
          draft={editingDraft}
          onClose={() => setEditingDraft(null)}
          onSave={handleSaveDraft}
        />
      )}

      {/* Settings modal */}
      {showSettings && (
        <SettingsModal
          config={intakeConfig}
          onClose={() => setShowSettings(false)}
          onSave={handleSaveSettings}
        />
      )}
    </div>
  );
}

function DraftCard({ draft, onPostNow, onSchedule, onCancel, onEdit }) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  const statusColor = {
    draft: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
    scheduled: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    posted: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    failed: 'bg-red-500/10 text-red-300 border-red-500/30',
    cancelled: 'bg-[#1E3A5F] text-[#9CB0C7] border-[#1E3A5F]',
  }[draft.status] || 'bg-[#1E3A5F] text-[#9CB0C7]';

  return (
    <div className="p-4 rounded-lg bg-[#0E1426] border border-[#1E3A5F]">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold truncate">{draft.title || `Thread ${draft.draft_id.slice(-6)}`}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusColor}`}>
              {draft.status.toUpperCase()}
            </span>
          </div>
          <p className="text-xs text-[#9CB0C7]">
            {draft.tweets.length} tweet{draft.tweets.length !== 1 ? 's' : ''} · created {new Date(draft.created_at).toLocaleString()}
            {draft.scheduled_for && ` · scheduled for ${new Date(draft.scheduled_for).toLocaleString()}`}
          </p>
        </div>
        {(draft.status === 'draft' || draft.status === 'scheduled') && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onEdit} className="p-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors" title="Edit">
              <Edit3 className="w-4 h-4" />
            </button>
            <button onClick={() => setShowSchedule(!showSchedule)} className="p-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors" title="Schedule">
              <Clock className="w-4 h-4" />
            </button>
            <button onClick={() => onPostNow(draft.draft_id)} className="px-3 py-2 rounded-md bg-[#1DA1F2] text-white text-xs font-semibold hover:bg-[#1DA1F2]/90 transition-colors flex items-center gap-1">
              <Send className="w-3 h-3" />
              Post Now
            </button>
            <button onClick={() => onCancel(draft.draft_id)} className="p-2 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors" title="Cancel">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {showSchedule && (
        <div className="mb-3 flex items-center gap-2">
          <input
            type="datetime-local"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            className="px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm"
          />
          <button
            onClick={() => { onSchedule(draft.draft_id, new Date(scheduleAt).toISOString()); setShowSchedule(false); }}
            disabled={!scheduleAt}
            className="px-3 py-2 rounded-md bg-amber-500 text-[#0A1628] text-sm font-semibold disabled:opacity-50"
          >
            Schedule
          </button>
        </div>
      )}

      <div className="space-y-2">
        {draft.tweets.map((t, i) => (
          <div key={i} className="flex items-start gap-2 p-3 rounded-md bg-[#0A1628] border border-[#1E3A5F]/50">
            <span className="text-[10px] text-[#6B7F95] font-mono mt-0.5">{i + 1}.</span>
            <p className="text-sm flex-1 whitespace-pre-wrap">{t}</p>
            <span className="text-[10px] text-[#6B7F95] flex-shrink-0">{t.length}c</span>
          </div>
        ))}
      </div>

      {draft.failure_reason && (
        <div className="mt-3 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-xs text-red-300">
          ⚠ {draft.failure_reason}
        </div>
      )}
    </div>
  );
}

function PasteModal({ subject, setSubject, text, setText, onCancel, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0E1426] rounded-lg border border-[#1E3A5F] max-w-2xl w-full p-6">
        <h2 className="text-xl font-bold mb-4">Paste Grok Content</h2>
        <input
          type="text"
          placeholder="Subject (optional)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm mb-3"
        />
        <textarea
          placeholder="Paste your Grok email or article content here..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm mb-4 font-mono"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-[#9CB0C7] hover:text-white transition-colors">
            Cancel
          </button>
          <button onClick={onSubmit} disabled={!text.trim()} className="px-4 py-2 rounded-md bg-[#3EF1C7] text-[#0A1628] font-semibold disabled:opacity-50">
            Format & Save Draft
          </button>
        </div>
      </div>
    </div>
  );
}

function EditDraftModal({ draft, onClose, onSave }) {
  const [tweets, setTweets] = useState(draft.tweets);

  const updateTweet = (i, val) => {
    const next = [...tweets];
    next[i] = val;
    setTweets(next);
  };

  const removeTweet = (i) => {
    setTweets(tweets.filter((_, idx) => idx !== i));
  };

  const addTweet = () => {
    setTweets([...tweets, '']);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0E1426] rounded-lg border border-[#1E3A5F] max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Edit Thread — {draft.title}</h2>
        <div className="space-y-2 mb-4">
          {tweets.map((t, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-xs text-[#6B7F95] font-mono mt-3 w-6">{i + 1}.</span>
              <textarea
                value={t}
                onChange={(e) => updateTweet(i, e.target.value)}
                rows={3}
                className={`flex-1 px-3 py-2 rounded-md bg-[#0A1628] border text-white text-sm ${
                  t.length > 280 ? 'border-red-500' : 'border-[#1E3A5F]'
                }`}
              />
              <div className="flex flex-col items-end gap-1 mt-1">
                <span className={`text-xs ${t.length > 280 ? 'text-red-400' : 'text-[#6B7F95]'}`}>
                  {t.length}/280
                </span>
                <button onClick={() => removeTweet(i)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addTweet} className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md text-[#3EF1C7] hover:bg-[#1E3A5F]/50">
          <Plus className="w-4 h-4" /> Add tweet
        </button>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-[#9CB0C7] hover:text-white">
            Cancel
          </button>
          <button
            onClick={() => onSave(draft.draft_id, tweets.filter((t) => t.trim()))}
            disabled={tweets.some((t) => t.length > 280) || tweets.length === 0}
            className="px-4 py-2 rounded-md bg-[#3EF1C7] text-[#0A1628] font-semibold disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ config, onClose, onSave }) {
  const [form, setForm] = useState({
    imap_enabled: config?.imap_enabled || false,
    imap_host: config?.imap_host || '',
    imap_port: config?.imap_port || 993,
    imap_username: config?.imap_username || '',
    imap_password: '',
    imap_folder: config?.imap_folder || 'INBOX',
    imap_sender_filter: config?.imap_sender_filter || 'noreply@x.ai',
    auto_post: config?.auto_post || false,
    default_post_delay_minutes: config?.default_post_delay_minutes || 0,
  });

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0E1426] rounded-lg border border-[#1E3A5F] max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Twitter Agent Settings</h2>

        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3 text-[#3EF1C7]">Auto-posting</h3>
          <label className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={form.auto_post} onChange={(e) => setForm({ ...form, auto_post: e.target.checked })} />
            <span className="text-sm">Auto-post drafts immediately after intake (skip approval)</span>
          </label>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3 text-[#3EF1C7]">IMAP Email Polling</h3>
          <label className="flex items-center gap-2 mb-3">
            <input type="checkbox" checked={form.imap_enabled} onChange={(e) => setForm({ ...form, imap_enabled: e.target.checked })} />
            <span className="text-sm">Poll an email inbox for Grok messages every 5 minutes</span>
          </label>
          {form.imap_enabled && (
            <div className="space-y-2 pl-6">
              <input className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm" placeholder="IMAP host (e.g. imap.gmail.com)" value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} />
              <input className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm" type="number" placeholder="Port" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) })} />
              <input className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm" placeholder="Username" value={form.imap_username} onChange={(e) => setForm({ ...form, imap_username: e.target.value })} />
              <input className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm" type="password" placeholder="Password (or app password)" value={form.imap_password} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} />
              <input className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm" placeholder="Folder (default INBOX)" value={form.imap_folder} onChange={(e) => setForm({ ...form, imap_folder: e.target.value })} />
              <input className="w-full px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm" placeholder="Sender filter (e.g. noreply@x.ai)" value={form.imap_sender_filter} onChange={(e) => setForm({ ...form, imap_sender_filter: e.target.value })} />
            </div>
          )}
        </div>

        {config?.webhook_url && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3 text-[#3EF1C7]">Webhook URL</h3>
            <code className="block text-xs p-3 bg-[#0A1628] border border-[#1E3A5F] rounded-md break-all">
              {window.location.origin}{config.webhook_url}
            </code>
            <p className="text-xs text-[#9CB0C7] mt-2">POST {`{ "subject": "...", "text": "..." }`} to this URL — no auth required, but URL is secret.</p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-[#9CB0C7] hover:text-white">
            Cancel
          </button>
          <button onClick={() => onSave(form)} className="px-4 py-2 rounded-md bg-[#3EF1C7] text-[#0A1628] font-semibold">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
